import { useCallback, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";

// Flags a nivel de módulo — sobreviven remounts del componente PushBootstrap.
// Sin esto:
//   - listenersInited=useRef se resetea en unmount → remount re-agrega los 4 listeners
//     nativos duplicados (callbacks disparan 2x, 3x, ...).
//   - permisosConcedidos: permisos y categorias se piden una sola vez por proceso.
//
// El token, en cambio, se pide en CADA initPush (cada login). Motivo: al hacer
// logout el contexto del portal se resetea y el token guardado en el reducer vuelve
// a null. Si el segundo login no lo pide de nuevo, el dispositivo se registra con
// fcm_token=null (bug: cambiar de usuario sin cerrar la app dejaba sin push).
// getToken() es barato: el SDK devuelve el token cacheado.
let listenersInited = false;
let permisosConcedidos = false;
let categoriesRegistered = false;
// Evita dos getToken() en paralelo si initPush se dispara dos veces seguidas.
let initPushEnCurso = null;

// Categorias de notification actions. Deben coincidir con el string que el back
// manda en Apns.Aps.Category y en data.categoria (ver FirebasePushSender.cs).
// El actionId es lo que llega en notificationActionPerformed.actionId cuando el
// user tapea el boton — lo interpreta el handler en PushBootstrap.
const NOTIFICATION_CATEGORIES = [
  {
    id: "recordatorio-turno",
    actions: [
      { id: "como-llegar", title: "Cómo llegar", destructive: false, foreground: true },
      { id: "ver-preparacion", title: "Ver preparación", destructive: false, foreground: true },
    ],
  },
];

/**
 * Hook para cablear @capacitor-firebase/messaging.
 *
 * Uso:
 *   const { initListeners, initPush, removeFcmToken, fcmTokenRef } =
 *     useFCMService({ onTokenUpdate, onPushReceived, onPushAction });
 *
 *   useEffect(() => { initListeners(); }, []);            // MOUNT temprano
 *   useEffect(() => { if (isAuthed) initPush(); }, [isAuthed]); // POST-login
 *
 * - onTokenUpdate(token: string | null): callback cuando FCM entrega/expira el token.
 * - onPushReceived(notification): callback en foreground (para refetch de la lista).
 * - onPushAction({ data, actionId }): callback cuando el user tapea la notificación
 *     o uno de sus botones de acción. actionId === "tap" para el banner completo,
 *     o el id del boton (ej "como-llegar", "ver-preparacion").
 *
 * Los listeners son idempotentes: llamar initListeners() más de una vez no
 * los duplica (flag a nivel módulo). Motivo del split entre initListeners e initPush:
 *   - initListeners se llama al MONTAR el Router (aunque el user no esté logueado),
 *     para no perder el evento notificationActionPerformed cuando iOS
 *     arranca la app desde killed por un tap en la noti.
 *   - initPush se llama SOLO cuando el user está autenticado: solicita permisos,
 *     registra categorias y hace getToken() que devuelve el FCM token.
 */
const useFCMService = ({ onTokenUpdate, onPushReceived, onPushAction } = {}) => {
  const fcmTokenRef = useRef(null);

  const initListeners = useCallback(async () => {
    if (Capacitor.getPlatform() === "web") return;
    if (listenersInited) return;
    listenersInited = true;

    await FirebaseMessaging.addListener("tokenReceived", (event) => {
      const value = event?.token || null;
      fcmTokenRef.current = value;
      if (onTokenUpdate) onTokenUpdate(value);
    });

    await FirebaseMessaging.addListener("notificationReceived", (event) => {
      // Foreground receive: event.notification tiene {title, body, data, id, ...}
      if (onPushReceived) onPushReceived(event?.notification);
    });

    await FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
      // event.actionId === "tap" -> user toco el banner (comportamiento anterior).
      // event.actionId === "<action id>" -> user toco un boton (recordatorio-turno).
      const data = event?.notification?.data || {};
      const actionId = event?.actionId || "tap";
      if (onPushAction) onPushAction({ data, actionId });
    });
  }, [onTokenUpdate, onPushReceived, onPushAction]);

  const initPush = useCallback(() => {
    if (Capacitor.getPlatform() === "web") return Promise.resolve();
    if (initPushEnCurso) return initPushEnCurso;

    initPushEnCurso = (async () => {
      try {
        // Permisos: solo la primera vez que se conceden. Si el user los rechazo,
        // se vuelven a chequear en el proximo login (puede habilitarlos en Ajustes).
        if (!permisosConcedidos) {
          let permStatus = await FirebaseMessaging.checkPermissions();
          if (permStatus.receive === "prompt" || permStatus.receive === "prompt-with-rationale") {
            permStatus = await FirebaseMessaging.requestPermissions();
          }
          if (permStatus.receive !== "granted") {
            return;
          }
          permisosConcedidos = true;
        }

        // Registrar las categorias iOS/Android antes de pedir el token — asi cuando
        // llega la primera noti con Category="recordatorio-turno", el sistema ya sabe
        // que botones mostrar. Idempotente vía flag a nivel modulo.
        if (!categoriesRegistered) {
          try {
            await FirebaseMessaging.setNotificationCategories?.({ categories: NOTIFICATION_CATEGORIES });
            categoriesRegistered = true;
          } catch (e) {
            // Si el plugin instalado no soporta la API (versiones viejas), seguimos
            // sin categorias — el push llega como banner simple sin botones.
            console.warn("setNotificationCategories no disponible", e);
          }
        }

        // Siempre: el token se pide en cada login (ver comentario de los flags).
        const result = await FirebaseMessaging.getToken();
        const value = result?.token || null;
        if (value) {
          fcmTokenRef.current = value;
          if (onTokenUpdate) onTokenUpdate(value);
        }
      } catch (e) {
        console.error("initPush error", e);
      } finally {
        initPushEnCurso = null;
      }
    })();

    return initPushEnCurso;
  }, [onTokenUpdate]);

  const removeFcmToken = useCallback(() => {
    fcmTokenRef.current = null;
    if (onTokenUpdate) onTokenUpdate(null);
  }, [onTokenUpdate]);

  return { initListeners, initPush, removeFcmToken, fcmTokenRef };
};

export default useFCMService;
